/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Selectors as SubscriptionSelectors } from 'core/store/ducks/entitlements/subscription';
import {
  ActionCreators as SubscriptionsFoldersCreators,
  Selectors as SubscriptionsFoldersSelectors,
} from 'core/store/ducks/entitlements/assets/subscriptionsFolders';
import { Selectors as GDPRSelectors } from 'core/store/ducks/gdpr';
import { StoreState } from 'store/store';

import {
  MenuUiArea,
  MenuItem,
  EntryPoint,
  MenuItemType,
  FoldersUtils,
  Ducks,
  Specs,
  DisabledFeaturesValues,
} from '@wit/vtv-x-foundation-lib';
import MyTVFactory from 'ui/components/MyTv/MyTVFactory';

export interface RailMetaData {
  item: MenuItem;
  hasMore?: boolean;
  entryPoint?: EntryPoint;
}

export const RAIL_SUPPORTED_MENU_ITEM_TYPES = [
  MenuItemType.RECORDINGS,
  MenuItemType.SCHEDULE_RECORDINGS,
  MenuItemType.WISHLIST,
  MenuItemType.RENTALS,
  MenuItemType.SUBSCRIPTIONS,
  MenuItemType.CONTINUE_WATCHING,
];

export const RAIL_SUPPORTED_EXTERNAL_ITEM_TYPES = [
  ...Specs.MENU_ITEM_CONT_WATCHING_NAME,
  ...Specs.MENU_ITEM_REC_FOR_YOU_NAME,
];

export default function MainPage(): JSX.Element {
  const dispatch = useDispatch();

  const [rails, setRails] = useState<RailMetaData[]>([]);
  const [processedRails, setProcessedRails] = useState(false);

  const menu = useSelector(Ducks.Menu.Selectors.getMenu);
  const myTVRails = menu && menu[MenuUiArea.MY_TV];

  const subscriptionEntitlements = useSelector(SubscriptionSelectors.getSubscriptionEntitlements);
  const subscriptionsFolders = useSelector(SubscriptionsFoldersSelectors.getFolders);
  const isSubscriptionFoldersProcessed = useSelector(SubscriptionsFoldersSelectors.isProcessed);
  const isRecommendationsEnabled = useSelector(Ducks.Recommendations.Selectors.isFeatureEnabled);
  const isOptIn = useSelector(GDPRSelectors.hasAgreedToConsent);
  const isRecordingsDisabled = useSelector((store: StoreState) =>
    Ducks.Dms.Selectors.isFeatureDisabled(store, DisabledFeaturesValues.NO_PVR)
  );

  const getEntryPoint = (type?: MenuItemType): EntryPoint | undefined => {
    switch (type) {
      case MenuItemType.RECORDINGS:
        return EntryPoint.RECORDINGS;
      case MenuItemType.SCHEDULE_RECORDINGS:
        return EntryPoint.SCHEDULED_RECORDINGS;
      case MenuItemType.WISHLIST:
        return EntryPoint.WISHLIST;
      case MenuItemType.RENTALS:
      case MenuItemType.SUBSCRIPTIONS:
        return EntryPoint.SUBSCRIPTIONS;
      case MenuItemType.CONTINUE_WATCHING:
        return EntryPoint.WATCH_NEXT;
      default:
        return undefined;
    }
  };

  useEffect(() => {
    let initRails: RailMetaData[] = [];
    let hasWatchNextRail = false;
    let hasRecommendationWatchNext = false;
    let hasRecommentationsRails = false;

    myTVRails?.children.forEach((childItem) => {
      if (childItem.type && RAIL_SUPPORTED_MENU_ITEM_TYPES.includes(childItem.type)) {
        if (childItem.type === MenuItemType.SUBSCRIPTIONS && subscriptionEntitlements) {
          dispatch(SubscriptionsFoldersCreators.getSubscriptionsFolders(subscriptionEntitlements.map((e) => e.id)));
        }

        if (childItem.type === MenuItemType.RECORDINGS || childItem.type === MenuItemType.SCHEDULE_RECORDINGS) {
          if (!isRecordingsDisabled) {
            initRails.push({
              item: childItem,
              entryPoint: getEntryPoint(childItem.type),
            });
          }
        } else {
          initRails.push({
            item: childItem,
            entryPoint: getEntryPoint(childItem.type),
          });
        }

        if (childItem.type === MenuItemType.CONTINUE_WATCHING) {
          hasWatchNextRail = true;
        }
      } else if (isRecommendationsEnabled && childItem.type && childItem.type === MenuItemType.EXTERNAL) {
        if (RAIL_SUPPORTED_EXTERNAL_ITEM_TYPES.includes(childItem.name.toLocaleLowerCase())) {
          hasRecommentationsRails = true;
          initRails.push({
            item: childItem,
            entryPoint: getEntryPoint(childItem.type),
          });
        }

        if (Specs.MENU_ITEM_CONT_WATCHING_NAME.includes(childItem.name.toLocaleLowerCase())) {
          hasRecommendationWatchNext = true;
        }
      }
    });

    // check for both watch next rails (recommendations and not recommendations), and delete the watch next if both exist
    if (hasWatchNextRail && (hasRecommendationWatchNext || isRecommendationsEnabled)) {
      initRails = initRails.filter((rail) => rail.item.type !== MenuItemType.CONTINUE_WATCHING);
    }

    // If user is opt-out, change the order of the rails to show the recordings rail in the first place
    if (!isOptIn && hasRecommentationsRails) {
      let recordingsRail;
      for (let i = 0; i < initRails.length; i += 1) {
        if (initRails[i].item.type && initRails[i].item.type === MenuItemType.RECORDINGS) {
          [recordingsRail] = initRails.splice(i, 1);
          break;
        }
      }
      if (recordingsRail) {
        initRails.unshift(recordingsRail);
      }
    }

    setRails(initRails);
    setProcessedRails(true);
  }, []);

  useEffect(() => {
    if (!processedRails) {
      return;
    }
    if (isSubscriptionFoldersProcessed) {
      const updatedRails: RailMetaData[] = [];
      rails.forEach((rail) => {
        if (rail.item.type === MenuItemType.SUBSCRIPTIONS) {
          subscriptionsFolders.forEach((folder) => {
            const channelId = FoldersUtils.getMainChannelId(folder);
            updatedRails.push({
              item: {
                id: folder.id,
                name: folder.title,
                type: MenuItemType.SUBSCRIPTIONS,
                channelId,
                parent: rail.item,
                children: [],
                parentalRating: folder.parentalRating,
              },
              entryPoint: getEntryPoint(MenuItemType.SUBSCRIPTIONS),
            });
          });
        } else {
          updatedRails.push(rail);
        }
      });

      setRails(updatedRails);
    }
  }, [processedRails, isSubscriptionFoldersProcessed]);

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);
  return (
    <div className="mytv">
      <div className="content-container" id="MyTv">
        {rails.map((item) => {
          return <MyTVFactory key={item.item.id} rail={item} />;
        })}
      </div>
    </div>
  );
}
